import type { Event, EventHint } from "@sentry/core";
import type { PostHog } from "posthog-js";
import { getEnvPublic } from "@remi/environment";

import { getErrorReporter } from "../error-reporter/ErrorReporter";

export const POSTHOG_SESSION_COOKIE_NAME = "remi-posthog-session-id";
const POSTHOG_PROJECT_ID = "30464";

type PostHogContext = {
  session_id?: string;
  replay_url?: string;
};

/**
 * Sync PostHog session ID to our own cookie so backend can access it
 * Sets cookie with 10 minute expiration to ensure it gets refreshed
 */
export function syncPostHogSessionIdToCookie(sessionId: string): void {
  if (typeof document === "undefined") {
    return;
  }

  // Set cookie with 10 minute expiration - short enough to require refresh
  const expires = new Date();
  expires.setTime(expires.getTime() + 10 * 60 * 1000);

  // Add Secure flag only if we're on HTTPS (production)
  const isSecure = typeof window !== "undefined" && window.location.protocol === "https:";
  const secureFlag = isSecure ? "; Secure" : "";

  const cookieValue = `${POSTHOG_SESSION_COOKIE_NAME}=${sessionId}; path=/; expires=${expires.toUTCString()}; SameSite=Lax${secureFlag}`;
  // Setting cookie is necessary for backend access - we control the value so it's safe
  // oxlint-disable-next-line no-document-cookie -- Cookie assignment is safe, we control the value
  document.cookie = cookieValue;
}

/**
 * Create PostHog context from session ID
 * Constructs the replay URL using the PostHog host and project ID
 * Format: https://us.posthog.com/project/{project_id}/replay/{session_id}
 */
function createPostHogContextFromSessionId(sessionId: string): PostHogContext {
  const host = getEnvPublic("NEXT_PUBLIC_POSTHOG_HOST");

  const replayUrl = host ? `${host}/project/${POSTHOG_PROJECT_ID}/replay/${sessionId}` : undefined;

  return {
    session_id: sessionId,
    replay_url: replayUrl,
  };
}

/**
 * Get PostHog session ID from browser's PostHog instance
 * Returns null if PostHog is not available
 */
function getPostHogSessionIdFromBrowser(): string | null {
  if (typeof window === "undefined" || !("posthog" in window)) {
    return null;
  }

  // oxlint-disable-next-line remi/no-type-assertions -- window.posthog is dynamically attached
  const posthog = window.posthog as PostHog | undefined;

  return posthog?.get_session_id() ?? null;
}

/**
 * Get PostHog session ID from our own cookie (frontend)
 * Returns null if cookie doesn't exist
 */
function getPostHogSessionIdFromCookies(): string | null {
  if (typeof document === "undefined" || !document.cookie) {
    return null;
  }

  const cookiePrefix = `${POSTHOG_SESSION_COOKIE_NAME}=`;
  const cookies = document.cookie.split(";");

  for (const cookie of cookies) {
    const trimmedCookie = cookie.trim();
    if (trimmedCookie.startsWith(cookiePrefix)) {
      return trimmedCookie.substring(cookiePrefix.length);
    }
  }

  return null;
}

/**
 * Get PostHog context from browser PostHog instance or cookies (frontend only)
 * Syncs cookie if browser instance has a different session ID
 */
function getPostHogContext(): PostHogContext | null {
  const browserSessionId = getPostHogSessionIdFromBrowser();
  const cookieSessionId = getPostHogSessionIdFromCookies();

  // Normalize session IDs - filter out empty strings
  const validBrowserSessionId = browserSessionId?.trim() || null;
  const validCookieSessionId = cookieSessionId?.trim() || null;

  // If browser has a session ID and it doesn't match the cookie (or cookie is missing), sync it
  if (validBrowserSessionId && validBrowserSessionId !== validCookieSessionId) {
    syncPostHogSessionIdToCookie(validBrowserSessionId);
    return createPostHogContextFromSessionId(validBrowserSessionId);
  }

  // Use cookie session ID if available
  if (validCookieSessionId) {
    return createPostHogContextFromSessionId(validCookieSessionId);
  }

  // Fallback to browser session ID if cookie is missing
  if (validBrowserSessionId) {
    syncPostHogSessionIdToCookie(validBrowserSessionId);
    return createPostHogContextFromSessionId(validBrowserSessionId);
  }

  return null;
}

/**
 * beforeSend hook that adds PostHog context to Sentry events (frontend)
 * Usage:
 * ```ts
 * Sentry.init({
 *   beforeSend: addPostHogContextToSentryEvent,
 * });
 * ```
 */
export function addPostHogContextToSentryEvent<T extends Event>(event: T, _hint: EventHint): T | null {
  try {
    // Try to get context from PostHog instance or cookie
    let posthogContext = getPostHogContext();

    // Fallback: if getPostHogContext() failed but cookie exists, read directly from cookie
    if (!posthogContext?.session_id) {
      const cookieSessionId = getPostHogSessionIdFromCookies();
      if (cookieSessionId) {
        posthogContext = createPostHogContextFromSessionId(cookieSessionId);
      }
    }

    // Add context if we have a session ID
    if (posthogContext?.session_id) {
      event.contexts = {
        ...(event.contexts ?? {}),
        posthog: {
          type: "posthog",
          ...posthogContext,
        },
      };
    }
  } catch {
    // Silently fail - don't break Sentry error reporting if PostHog context fails
  }

  return event;
}

/**
 * Set PostHog context in Sentry (backend)
 * Similar to setUserInSentry - sets the PostHog session ID and replay URL in Sentry's context
 */
export function setPostHogContextInSentry(sessionId: string): void {
  const reporter = getErrorReporter();
  if (!reporter) {
    return;
  }

  const posthogContext = createPostHogContextFromSessionId(sessionId);
  reporter.setContext("posthog", {
    type: "posthog",
    ...posthogContext,
  });
}

/**
 * Clear PostHog context from Sentry
 * Similar to clearUserInSentry - removes PostHog context from Sentry's scope
 */
export function clearPostHogContextInSentry(): void {
  const reporter = getErrorReporter();
  if (!reporter) {
    return;
  }

  reporter.setContext("posthog", null);
}
